import type { LifeSpace, Project, Task } from '@/src/renderer/lib/db/models'
import { LIFE_SPACES, type BackupTables } from './format'
import { exportDate, fileNamePart, htmlToMarkdown, plainText, uniquePaths, type Translate } from './exportText'

/**
 * **Export als Markdown** (#252): lesbar, zum Mitnehmen nach Obsidian, Notion oder einfach zum Lesen.
 *
 * - Je Projekt eine Datei `Lebensbereich/Projekt.md`, Unterprojekte im Ordner ihres Projekts.
 * - Abschnitte als `##`, Aufgaben als `- [ ]`, Unteraufgaben eingerückt, Beschreibung darunter; Datum und
 *   Tags in der Zeile. Pinnwand-Notizen als Liste am Ende.
 * - „Kurz notiert“, Ziele und Gewohnheiten je Lebensbereich als eigene Datei.
 * - Erledigtes nur mit Haken (Entscheidung 18): dann je Abschnitt ein Block „Erledigt“, neueste zuerst.
 *
 * Einstellungen und Profil gibt es hier nicht.
 */

export interface MarkdownExportInput {
  tables: BackupTables
  includeCompleted: boolean
  t: Translate
}

const isDone = (task: Task) => task.status === 'completed' || task.completedAt !== null
const byOrder = <T extends { order: number }>(a: T, b: T) => a.order - b.order
const newestFirst = (a: Task, b: Task) => (b.completedAt?.getTime() ?? 0) - (a.completedAt?.getTime() ?? 0)

/** Text unter einer Aufgabe oder Notiz: jede Zeile eingerückt, damit Markdown sie zum Punkt zählt. */
const indentBlock = (text: string, indent: string) => text.split('\n').map(line => (line ? `${indent}${line}` : '')).join('\n')

export function buildMarkdownFiles({ tables, includeCompleted, t }: MarkdownExportInput): Record<string, string> {
  const md = (key: string) => t(`preferences.export.file.md.${key}`)
  const tagName = new Map(tables.tags.map(tag => [tag.id, tag.name]))
  const childrenOf = new Map<string, Task[]>()
  for (const task of tables.tasks) {
    if (!task.parentTaskId) continue
    const list = childrenOf.get(task.parentTaskId) ?? []
    list.push(task)
    childrenOf.set(task.parentTaskId, list)
  }
  const shownIds = new Set(tables.tasks.filter(task => includeCompleted || !isDone(task)).map(task => task.id))
  // Eine Unteraufgabe, deren Aufgabe nicht dasteht (nicht gewählt, oder erledigt ohne Haken), steht oben in ihrer Gruppe.
  const isTopLevel = (task: Task) => !task.parentTaskId || !shownIds.has(task.parentTaskId)

  const taskLine = (task: Task, depth: number): string[] => {
    const indent = '  '.repeat(depth)
    const meta = [
      task.startDate && `${md('start')} ${exportDate(task.startDate)}`,
      task.dueDate && `${md('due')} ${exportDate(task.dueDate)}`,
      isDone(task) && task.completedAt && `${md('completedOn')} ${exportDate(task.completedAt)}`,
    ].filter(Boolean)
    const tags = task.tags.map(id => tagName.get(id)).filter(Boolean).map(name => `#${name!.replace(/\s+/g, '-')}`)
    const title = task.title.replace(/\s*\n\s*/g, ' ').trim() || '—'
    const line = `${indent}- [${isDone(task) ? 'x' : ' '}] ${title}${meta.length ? ` — ${meta.join(', ')}` : ''}${tags.length ? ` ${tags.join(' ')}` : ''}`
    const description = plainText(task.description)
    const children = (childrenOf.get(task.id) ?? [])
      .filter(child => includeCompleted || !isDone(child))
      .sort(byOrder)
      .flatMap(child => taskLine(child, depth + 1))
    return [line, ...(description ? [indentBlock(description, `${indent}  `)] : []), ...children]
  }

  /** Offene Aufgaben in ihrer Reihenfolge; mit Haken darunter „Erledigt“, neueste zuerst. */
  const taskGroup = (tasks: readonly Task[]): string[] => {
    const top = tasks.filter(isTopLevel)
    const open = top.filter(task => !isDone(task)).sort(byOrder).flatMap(task => taskLine(task, 0))
    const done = includeCompleted ? top.filter(isDone).sort(newestFirst).flatMap(task => taskLine(task, 0)) : []
    return [
      ...(open.length ? [open.join('\n')] : []),
      ...(done.length ? [`### ${md('completedHeading')}`, done.join('\n')] : []),
    ]
  }

  const projectFile = (project: Project): string => {
    const tasks = tables.tasks.filter(task => task.projectId === project.id)
    const sections = tables.sections.filter(section => section.projectId === project.id).sort(byOrder)
    const sectionIds = new Set(sections.map(section => section.id))
    const notes = tables.canvasNotes.filter(note => note.projectId === project.id && note.kind !== 'area')
    const description = plainText(project.description)
    const blocks = [
      `# ${project.name}`,
      ...(description ? [description] : []),
      ...taskGroup(tasks.filter(task => !task.sectionId || !sectionIds.has(task.sectionId))),
      ...sections.flatMap(section => [`## ${section.title}`, ...taskGroup(tasks.filter(task => task.sectionId === section.id))]),
      ...(notes.length
        ? [`## ${md('canvas')}`, notes.map(note => {
            // Notiz und Memo sind Klartext (das Memo schreibt selbst Markdown); nur der Text ist HTML.
            const text = note.kind === 'text' ? htmlToMarkdown(note.content) : plainText(note.content)
            const head = note.title ? `- **${note.title}**` : `- ${text.split('\n')[0] ?? ''}`
            const rest = note.title ? text : text.split('\n').slice(1).join('\n')
            return rest ? `${head}\n${indentBlock(rest, '  ')}` : head
          }).join('\n')]
        : []),
    ]
    return `${blocks.join('\n\n')}\n`
  }

  const files: Record<string, string> = {}
  const unique = uniquePaths()
  const projectIds = new Set(tables.projects.map(p => p.id))
  const projectById = new Map(tables.projects.map(p => [p.id, p]))
  const folderOf = (lifeSpace: LifeSpace) => fileNamePart(t(`lifeSpaces.${lifeSpace}`), lifeSpace)
  const pathCache = new Map<string, string>()
  /** Pfad ohne `.md`: Unterprojekte liegen im Ordner ihres Projekts. */
  const basePathOf = (project: Project, seen = new Set<string>()): string => {
    const cached = pathCache.get(project.id)
    if (cached) return cached
    const parent = project.parentId && projectIds.has(project.parentId) && !seen.has(project.parentId) ? projectById.get(project.parentId)! : null
    const dir = parent ? basePathOf(parent, new Set([...seen, project.id])) : folderOf(project.lifeSpace)
    const path = unique(`${dir}/${fileNamePart(project.name, 'Projekt')}.md`).replace(/\.md$/, '')
    pathCache.set(project.id, path)
    return path
  }

  for (const lifeSpace of LIFE_SPACES) {
    const folder = folderOf(lifeSpace)
    const projects = tables.projects.filter(p => p.lifeSpace === lifeSpace).sort(byOrder)
    for (const project of projects) files[`${basePathOf(project)}.md`] = projectFile(project)

    const quickNotes = tables.tasks.filter(task => !task.projectId && task.lifeSpace === lifeSpace)
    const quickGroup = taskGroup(quickNotes)
    if (quickGroup.length) files[unique(`${folder}/${fileNamePart(md('quickNotes'), 'Quick notes')}.md`)] = `${[`# ${md('quickNotes')}`, ...quickGroup].join('\n\n')}\n`

    const goals = tables.goals.filter(goal => goal.lifeSpace === lifeSpace)
    if (goals.length) {
      const list = goals.map(goal => {
        const why = plainText(goal.description)
        return why ? `- **${goal.title}**\n${indentBlock(why, '  ')}` : `- **${goal.title}**`
      })
      files[unique(`${folder}/${fileNamePart(md('goals'), 'Goals')}.md`)] = `# ${md('goals')}\n\n${list.join('\n')}\n`
    }

    const habits = tables.habits.filter(habit => habit.lifeSpace === lifeSpace).sort((a, b) => a.sortOrder - b.sortOrder)
    if (habits.length) {
      const list = habits.map(habit => {
        const notes = plainText(habit.notes)
        const paused = habit.pausedAt ? ` — ${md('paused')}` : ''
        return notes ? `- ${habit.title}${paused}\n${indentBlock(notes, '  ')}` : `- ${habit.title}${paused}`
      })
      files[unique(`${folder}/${fileNamePart(md('habits'), 'Habits')}.md`)] = `# ${md('habits')}\n\n${list.join('\n')}\n`
    }
  }
  return files
}
